import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useEffect } from 'react';

export interface PerformanceEvaluation {
  id: string;
  guard_id: string;
  evaluation_period_start: string;
  evaluation_period_end: string;
  patrol_performance_score: number;
  supervisor_reports_score: number;
  punctuality_score: number;
  assessment_score: number;
  client_evaluation_score: number;
  behavior_score: number;
  total_raw_score: number;
  weighted_score: number;
  performance_grade: 'A+' | 'A' | 'B+' | 'B' | 'C+' | 'C' | 'D' | 'F' | null;
  team_rank?: number;
  overall_rank?: number;
  evaluation_status: string;
  evaluated_by?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  guard?: {
    first_name: string;
    last_name: string;
    full_name?: string;
  };
}

export interface PerformanceDetail {
  id: string;
  evaluation_id: string;
  metric_type: string;
  metric_name: string;
  raw_value: number;
  baseline_value?: number;
  target_value?: number;
  points_awarded: number;
  max_possible_points: number;
  calculation_details: any;
  performance_notes?: string;
}

export interface Achievement {
  id: string;
  guard_id: string;
  evaluation_id?: string;
  achievement_type: string;
  achievement_name: string;
  achievement_description?: string;
  badge_icon?: string;
  badge_color: string;
  earned_date: string;
  valid_until?: string;
}

// Hook to fetch performance evaluations with optional filters
export const usePerformanceEvaluations = (options?: {
  guardId?: string;
  teamId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  realTime?: boolean;
}) => {
  const queryClient = useQueryClient();
  const queryKey = ['performance-evaluations', options];
  
  // Real-time subscription
  useEffect(() => {
    if (options?.realTime === false) return;
    
    let channelFilter = {};
    if (options?.guardId) {
      channelFilter = { filter: `guard_id=eq.${options.guardId}` };
    }
    
    const channel = supabase
      .channel('performance-evaluations-changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'guard_performance_evaluations', ...channelFilter },
        () => queryClient.invalidateQueries({ queryKey })
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [options?.guardId, options?.teamId, options?.realTime, queryClient, queryKey]);
  
  return useQuery({
    queryKey,
    queryFn: async (): Promise<PerformanceEvaluation[]> => {
      let query = supabase
        .from('guard_performance_evaluations')
        .select(`
          *,
          guard:profiles!guard_id(
            first_name,
            last_name,
            full_name
          )
        `)
        .order('weighted_score', { ascending: false });

      if (options?.guardId) {
        query = query.eq('guard_id', options.guardId);
      }

      if (options?.startDate) {
        query = query.gte('evaluation_period_start', options.startDate);
      }

      if (options?.endDate) {
        query = query.lte('evaluation_period_end', options.endDate);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      let finalData = data || [];

      // Filter by team if specified (post-process since we can't do complex subqueries)
      if (options?.teamId && finalData.length > 0) {
        const { data: teamMembers, error: teamError } = await supabase
          .from('team_members')
          .select('user_id')
          .eq('team_id', options.teamId);

        if (!teamError && teamMembers) {
          const teamMemberIds = teamMembers.map(tm => tm.user_id);
          finalData = finalData.filter(evaluation => 
            teamMemberIds.includes(evaluation.guard_id)
          );
        }
      }

      if (error) {
        console.error('Error fetching performance evaluations:', error);
        throw error;
      }

      return finalData as any;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to fetch performance evaluation details
export const usePerformanceDetails = (evaluationId: string) => {
  return useQuery({
    queryKey: ['performance-details', evaluationId],
    queryFn: async (): Promise<PerformanceDetail[]> => {
      const { data, error } = await supabase
        .from('guard_performance_details')
        .select('*')
        .eq('evaluation_id', evaluationId)
        .order('metric_type');

      if (error) {
        console.error('Error fetching performance details:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!evaluationId,
  });
};

// Hook to fetch achievements
export const useGuardAchievements = (guardId: string) => {
  return useQuery({
    queryKey: ['guard-achievements', guardId],
    queryFn: async (): Promise<Achievement[]> => {
      const { data, error } = await supabase
        .from('guard_performance_achievements')
        .select('*')
        .eq('guard_id', guardId)
        .order('earned_date', { ascending: false });

      if (error) {
        console.error('Error fetching achievements:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!guardId,
  });
};

// Hook to calculate performance evaluation
export const useCalculatePerformanceEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      guardId,
      startDate,
      endDate,
      saveEvaluation = false,
    }: {
      guardId: string;
      startDate: string;
      endDate: string;
      saveEvaluation?: boolean;
    }) => {
      const { data, error } = await supabase.functions.invoke('calculate-performance-evaluation', {
        body: {
          guard_id: guardId,
          start_date: startDate,
          end_date: endDate,
          save_evaluation: saveEvaluation,
        },
      });

      if (error) {
        console.error('Error calculating performance evaluation:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data, variables) => {
      if (variables.saveEvaluation) {
        toast.success('Αξιολόγηση υπολογίστηκε και αποθηκεύτηκε επιτυχώς!');
        // Invalidate related queries
        queryClient.invalidateQueries({ queryKey: ['performance-evaluations'] });
        queryClient.invalidateQueries({ 
          queryKey: ['performance-evaluations', { guardId: variables.guardId }] 
        });
      } else {
        toast.success('Αξιολόγηση υπολογίστηκε επιτυχώς!');
      }
    },
    onError: (error: any) => {
      console.error('Performance evaluation calculation failed:', error);
      toast.error(`Σφάλμα υπολογισμού αξιολόγησης: ${error.message || 'Άγνωστο σφάλμα'}`);
    },
  });
};

// Hook to update performance evaluation
export const useUpdatePerformanceEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      evaluationId,
      updates,
    }: {
      evaluationId: string;
      updates: Partial<PerformanceEvaluation>;
    }) => {
      const { data, error } = await supabase
        .from('guard_performance_evaluations')
        .update(updates)
        .eq('id', evaluationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating performance evaluation:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Αξιολόγηση ενημερώθηκε επιτυχώς!');
      queryClient.invalidateQueries({ queryKey: ['performance-evaluations'] });
    },
    onError: (error: any) => {
      console.error('Performance evaluation update failed:', error);
      toast.error(`Σφάλμα ενημέρωσης αξιολόγησης: ${error.message || 'Άγνωστο σφάλμα'}`);
    },
  });
};

// Hook to get performance rankings
export const usePerformanceRankings = (period?: 'weekly' | 'monthly' | 'quarterly') => {
  const now = new Date();
  let startDate: string;
  let endDate: string;

  switch (period) {
    case 'weekly':
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      startDate = weekStart.toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case 'quarterly':
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      startDate = quarterStart.toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case 'monthly':
    default:
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate = monthStart.toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
  }

  return usePerformanceEvaluations({
    startDate,
    endDate,
    limit: 50, // Top 50 performers
  });
};